import React, { useState, useRef } from 'react';
import { 
  User, 
  Mail, 
  Lock, 
  Camera, 
  Save, 
  Upload,
  Eye,
  EyeOff,
  Bell,
  Globe,
  Shield,
  Trash2,
  AlertCircle,
  CheckCircle,
  Settings
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

function AccountSettings() {
  const { user, updateProfile, changePassword, uploadAvatar, logout, isGuest } = useAuth();
  const fileInputRef = useRef(null);
  
  const [activeTab, setActiveTab] = useState('profile');
  const [profileForm, setProfileForm] = useState({
    displayName: user?.displayName || '',
    email: user?.email || '',
    bio: user?.bio || '',
  });
  
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  
  const [settingsForm, setSettingsForm] = useState({
    notifications: user?.settings?.notifications ?? true,
    emailUpdates: user?.settings?.emailUpdates ?? true,
    theme: user?.settings?.theme || 'dark',
    language: user?.settings?.language || 'en',
    timezone: user?.settings?.timezone || 'UTC',
  });
  
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  
  const [isLoading, setIsLoading] = useState({
    profile: false,
    password: false,
    avatar: false,
    settings: false,
  });
  
  const [message, setMessage] = useState({ type: '', text: '' });

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    
    try {
      setIsLoading(prev => ({ ...prev, profile: true }));
      await updateProfile(profileForm);
      showMessage('success', 'Profile updated successfully');
    } catch (error) {
      showMessage('error', error.message || 'Failed to update profile');
    } finally {
      setIsLoading(prev => ({ ...prev, profile: false }));
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      showMessage('error', 'New passwords do not match');
      return;
    }
    
    if (passwordForm.newPassword.length < 6) {
      showMessage('error', 'Password must be at least 6 characters long');
      return;
    }
    
    try {
      setIsLoading(prev => ({ ...prev, password: true }));
      await changePassword(passwordForm.currentPassword, passwordForm.newPassword);
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      showMessage('success', 'Password changed successfully');
    } catch (error) {
      showMessage('error', error.message || 'Failed to change password');
    } finally {
      setIsLoading(prev => ({ ...prev, password: false }));
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showMessage('error', 'File size must be less than 5MB');
      return;
    }
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      showMessage('error', 'Please select a valid image file');
      return;
    }
    
    try {
      setIsLoading(prev => ({ ...prev, avatar: true }));
      await uploadAvatar(file);
      showMessage('success', 'Avatar updated successfully');
    } catch (error) {
      showMessage('error', error.message || 'Failed to upload avatar');
    } finally {
      setIsLoading(prev => ({ ...prev, avatar: false }));
    }
  };

  const handleSettingsUpdate = async (e) => {
    e.preventDefault();
    
    try {
      setIsLoading(prev => ({ ...prev, settings: true }));
      await updateProfile({ settings: settingsForm });
      showMessage('success', 'Settings updated successfully');
    } catch (error) {
      showMessage('error', error.message || 'Failed to update settings');
    } finally {
      setIsLoading(prev => ({ ...prev, settings: false }));
    }
  };

  const handleDeleteAccount = async () => {
    if (window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      try {
        // In a real app, you would call a delete account API
        await logout();
        showMessage('success', 'Account deleted successfully');
      } catch (error) {
        showMessage('error', 'Failed to delete account');
      }
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'preferences', label: 'Preferences', icon: Settings },
    { id: 'notifications', label: 'Notifications', icon: Bell },
  ];

  if (isGuest()) {
    return (
      <div className="text-center py-12">
        <User className="w-16 h-16 text-gray-600 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-gray-300 mb-2">Guest Account</h3>
        <p className="text-gray-500 mb-6">
          Guest accounts have limited access to account settings. Create a full account to access all features.
        </p>
        <button className="btn-primary">
          Create Account
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">Account Settings</h1>
        <p className="text-gray-400">Manage your account preferences and security settings</p>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`p-4 rounded-lg flex items-center space-x-3 ${
          message.type === 'success'
            ? 'bg-green-500 bg-opacity-10 border border-green-500'
            : 'bg-red-500 bg-opacity-10 border border-red-500'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle className="w-5 h-5 text-green-400" />
          ) : (
            <AlertCircle className="w-5 h-5 text-red-400" />
          )}
          <p className={message.type === 'success' ? 'text-green-400' : 'text-red-400'}>
            {message.text}
          </p>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-700">
        <nav className="flex space-x-8">
          {tabs.map(tab => {
            const IconComponent = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                  activeTab === tab.id
                    ? 'border-cf-orange text-cf-orange'
                    : 'border-transparent text-gray-400 hover:text-white'
                }`}
              >
                <IconComponent className="w-5 h-5" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="card">
        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-white">Profile Information</h2>
            
            {/* Avatar Section */}
            <div className="flex items-center space-x-6">
              <div className="relative">
                <img
                  src={user?.avatar || `https://via.placeholder.com/100x100/0051c3/ffffff?text=${user?.displayName?.charAt(0) || 'U'}`}
                  alt="Avatar"
                  className="w-20 h-20 rounded-full object-cover"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isLoading.avatar}
                  className="absolute bottom-0 right-0 p-2 bg-cf-orange rounded-full text-white hover:bg-orange-600 transition-colors duration-200"
                >
                  {isLoading.avatar ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Camera className="w-4 h-4" />
                  )}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarUpload}
                  className="hidden"
                />
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-white">{user?.displayName}</h3>
                <p className="text-gray-400">{user?.email}</p>
                <p className="text-gray-500 text-sm">Click the camera icon to update your avatar</p>
              </div>
            </div>
            
            {/* Profile Form */}
            <form onSubmit={handleProfileUpdate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Display Name
                </label>
                <input
                  type="text"
                  value={profileForm.displayName}
                  onChange={(e) => setProfileForm(prev => ({ ...prev, displayName: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={profileForm.email}
                  onChange={(e) => setProfileForm(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Bio
                </label>
                <textarea
                  value={profileForm.bio}
                  onChange={(e) => setProfileForm(prev => ({ ...prev, bio: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                  rows="3"
                  placeholder="Tell us about yourself..."
                />
              </div>
              
              <button
                type="submit"
                disabled={isLoading.profile}
                className="btn-primary flex items-center space-x-2"
              >
                {isLoading.profile ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Save className="w-5 h-5" />
                )}
                <span>Save Changes</span>
              </button>
            </form>
          </div>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-white">Security Settings</h2>
            
            {/* Password Change */}
            <div>
              <h3 className="text-lg font-medium text-white mb-4">Change Password</h3>
              
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Current Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword.current ? 'text' : 'password'}
                      value={passwordForm.currentPassword}
                      onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                      className="w-full px-3 py-2 pr-10 bg-gray-700 border border-gray-600 rounded text-white"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(prev => ({ ...prev, current: !prev.current }))}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    >
                      {showPassword.current ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword.new ? 'text' : 'password'}
                      value={passwordForm.newPassword}
                      onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                      className="w-full px-3 py-2 pr-10 bg-gray-700 border border-gray-600 rounded text-white"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(prev => ({ ...prev, new: !prev.new }))}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    >
                      {showPassword.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword.confirm ? 'text' : 'password'}
                      value={passwordForm.confirmPassword}
                      onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      className="w-full px-3 py-2 pr-10 bg-gray-700 border border-gray-600 rounded text-white"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(prev => ({ ...prev, confirm: !prev.confirm }))}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    >
                      {showPassword.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                
                <button
                  type="submit"
                  disabled={isLoading.password}
                  className="btn-primary flex items-center space-x-2"
                >
                  {isLoading.password ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Lock className="w-5 h-5" />
                  )}
                  <span>Change Password</span>
                </button>
              </form>
            </div>
            
            {/* Danger Zone */}
            <div className="border-t border-gray-700 pt-6">
              <h3 className="text-lg font-medium text-red-400 mb-4">Danger Zone</h3>
              <div className="bg-red-500 bg-opacity-10 border border-red-500 rounded-lg p-4">
                <h4 className="text-red-400 font-medium mb-2">Delete Account</h4>
                <p className="text-gray-300 text-sm mb-4">
                  Once you delete your account, there is no going back. Please be certain.
                </p>
                <button
                  onClick={handleDeleteAccount}
                  className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors duration-200"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Delete Account</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Preferences Tab */}
        {activeTab === 'preferences' && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-white">Preferences</h2>
            
            <form onSubmit={handleSettingsUpdate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Theme
                </label>
                <select
                  value={settingsForm.theme}
                  onChange={(e) => setSettingsForm(prev => ({ ...prev, theme: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                >
                  <option value="dark">Dark</option>
                  <option value="light">Light</option>
                  <option value="auto">Auto</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Language
                </label>
                <select
                  value={settingsForm.language}
                  onChange={(e) => setSettingsForm(prev => ({ ...prev, language: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                >
                  <option value="en">English</option>
                  <option value="es">Español</option>
                  <option value="fr">Français</option>
                  <option value="de">Deutsch</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Timezone
                </label>
                <select
                  value={settingsForm.timezone}
                  onChange={(e) => setSettingsForm(prev => ({ ...prev, timezone: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                >
                  <option value="UTC">UTC</option>
                  <option value="America/New_York">Eastern Time</option>
                  <option value="America/Chicago">Central Time</option>
                  <option value="America/Denver">Mountain Time</option>
                  <option value="America/Los_Angeles">Pacific Time</option>
                </select>
              </div>
              
              <button
                type="submit"
                disabled={isLoading.settings}
                className="btn-primary flex items-center space-x-2"
              >
                {isLoading.settings ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Save className="w-5 h-5" />
                )}
                <span>Save Preferences</span>
              </button>
            </form>
          </div>
        )}

        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-white">Notification Settings</h2>
            
            <form onSubmit={handleSettingsUpdate} className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-white font-medium">Push Notifications</h3>
                  <p className="text-gray-400 text-sm">Receive notifications about stream events</p>
                </div>
                <input
                  type="checkbox"
                  checked={settingsForm.notifications}
                  onChange={(e) => setSettingsForm(prev => ({ ...prev, notifications: e.target.checked }))}
                  className="w-5 h-5 text-cf-orange bg-gray-700 border-gray-600 rounded focus:ring-cf-orange"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-white font-medium">Email Updates</h3>
                  <p className="text-gray-400 text-sm">Receive email updates about new features and streams</p>
                </div>
                <input
                  type="checkbox"
                  checked={settingsForm.emailUpdates}
                  onChange={(e) => setSettingsForm(prev => ({ ...prev, emailUpdates: e.target.checked }))}
                  className="w-5 h-5 text-cf-orange bg-gray-700 border-gray-600 rounded focus:ring-cf-orange"
                />
              </div>
              
              <button
                type="submit"
                disabled={isLoading.settings}
                className="btn-primary flex items-center space-x-2"
              >
                {isLoading.settings ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Save className="w-5 h-5" />
                )}
                <span>Save Notification Settings</span>
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

export default AccountSettings; 